import type { Edge, Node } from '@xyflow/react'
import { isWorkflowNodeType, WorkflowNodeType } from '../../nodes/types'

export type IncomingCreateDocumentKind = 'none' | 'document' | 'bank_transaction'

const PASSTHROUGH: ReadonlySet<WorkflowNodeType> = new Set([
  'wait',
  'email_send',
  'audit_trail_comment'
])

const nodeConfig = (node: Node): Record<string, unknown> => {
  const data = node.data as { config?: Record<string, unknown> } | undefined
  return data?.config ?? {}
}

const producedKind = (
  node: Node,
  nodes: Node[],
  edges: Edge[],
  visiting: Set<string>
): IncomingCreateDocumentKind => {
  if (!isWorkflowNodeType(node.type) || visiting.has(node.id)) return 'none'

  if (PASSTHROUGH.has(node.type)) {
    visiting.add(node.id)
    return resolveIncomingCreateDocumentKind(node.id, nodes, edges, visiting)
  }

  if (node.type === 'scheduled_trigger') return 'none'
  if (node.type === 'create_document' || node.type === 'luca') return 'document'

  if (node.type === 'event_trigger') {
    const eventType = nodeConfig(node).event_type
    if (eventType === 'BANK_TRANSACTION_CREATED') return 'bank_transaction'
    if (eventType === 'DOCUMENT_CREATED' || eventType === 'DOCUMENT_POSTED') return 'document'
    return 'none'
  }

  if (node.type === 'filter' || node.type === 'selector') {
    const config = nodeConfig(node)
    if (node.type === 'selector') {
      const conditions = config.conditions
      if (!Array.isArray(conditions) || conditions.length === 0) return 'none'
    }
    const filterType = config.filter_type
    if (filterType === 'bank_transaction') return 'bank_transaction'
    if (filterType === 'document') return 'document'
    return 'none'
  }

  return 'none'
}

export const resolveIncomingCreateDocumentKind = (
  nodeId: string,
  nodes: Node[],
  edges: Edge[],
  visiting: Set<string> = new Set()
): IncomingCreateDocumentKind => {
  const incoming = edges.filter(edge => edge.target === nodeId)
  let sawDocument = false
  for (const edge of incoming) {
    const source = nodes.find(node => node.id === edge.source)
    if (!source) continue
    const kind = producedKind(source, nodes, edges, visiting)
    if (kind === 'bank_transaction') return 'bank_transaction'
    if (kind === 'document') sawDocument = true
  }
  return sawDocument ? 'document' : 'none'
}
